import { FormControl } from '@mui/material'
import React, { useRef, useState } from 'react'
import { Typography, Input, Button } from '@evocolabs/orkaui'
import theme from '@evocolabs/orkaui/lib/theme.cjs'
import Link from 'next/link'
import i18n from 'i18next'
import { useRouter } from 'next/router'
import k from './../../i18n/keys'
import { useBackend } from '@/src/providers/BackendProvider'
import validateEmail from '@/src/utils/validateEmail'
import { postUTMHistory } from '@/src/providers/RemotePoint'

const SignUp = ({ signUpEmail, setSignUpEmail }) => {
  const [p1Values, setP1Values] = useState({
    email: signUpEmail,
    checkbox: false
  })

  const [errors, setErrors] = useState({
    email: '',
    checked: false
  })

  const {
    asyncUserEmailTakenStatusAll,
    asyncUserSignupFirst,
    sendVerifyEmail
  } = useBackend()

  const router = useRouter()

  const continueButtonRef = useRef(null)

  const checkInfo = () => {
    return errors.email === '' && p1Values.checkbox && p1Values.email !== ''
  }

  const handleChecked = (event) => {
    setP1Values({ ...p1Values, [event.target.name]: event.target.checked })
    if (event.target.checked) {
      setErrors((latestErrors) => {
        return { ...latestErrors, checked: '' }
      })
    }
  }

  const INVALID_INPUT_ERROR = 'invalid input'
  const ALREADY_EXIST_EMAIL = 'email already exists'

  const continueOnClick = async () => {
    try {
      if (p1Values.checkbox === false) {
        throw new Error('Checked')
      } else if (checkInfo()) {
        await postUTMHistory(p1Values.email)
        sessionStorage.setItem('signUpEmail', p1Values.email)
        setSignUpEmail(p1Values.email)
        const ifTaken = await asyncUserEmailTakenStatusAll(p1Values.email)
        if (ifTaken && ifTaken.exists && ifTaken.verified) {
          // true => taken
          throw new Error(ALREADY_EXIST_EMAIL)
        } else if (!ifTaken || (!ifTaken.exists && ifTaken.verified)) {
          await asyncUserSignupFirst(p1Values.email)
        }
        sendVerifyEmail(p1Values.email)
        continueOnSuccess()
      } else {
        throw new Error(INVALID_INPUT_ERROR)
      }
    } catch (e) {
      continueOnFailed(e)
    }
  }

  const continueOnSuccess = () => {
    router.push('/SignIn?InitialVerifyEmail')
  }

  const continueOnFailed = (Error) => {
    if (Error.message === 'Checked') {
      setErrors((latestErrors) => {
        return {
          ...latestErrors,
          checked: i18n.t(k.PLEASE_AGREE_TO_OUR_TERMS_OF_S1)
        }
      })
    } else if (Error.message === ALREADY_EXIST_EMAIL) {
      setErrors((latestErrors) => ({
        ...latestErrors,
        email: i18n.t(k.EMAIL_ADDRESS_ALREADY_EXISTS)
      }))
    }
    if (p1Values.email === '') {
      setErrors((latestErrors) => {
        return {
          ...latestErrors,
          email: i18n.t(k.EMPTY_EMAIL_ADDRESS_REQUIRED)
        }
      })
    } else if (!validateEmail(p1Values.email)) {
      setErrors((latestErrors) => {
        return { ...latestErrors, email: i18n.t(k.INVALID_EMAIL_ADDRESS1) }
      })
    }
  }

  const handleChange = (prop) => (event) => {
    setP1Values({ ...p1Values, [prop]: event.target.value })
    setErrors({ ...errors, [prop]: '' })
  }

  const handleBackToSignIn = () => {
    router.push('/SignIn')
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
        gap: '32px'
      }}
    >
      <div>
        <Typography variant="h4">{i18n.t(k.CREATE_AN_ACCOUNT)}</Typography>
      </div>
      <FormControl>
        <Input
          id="outlined-adornment-emailaddress"
          placeholder="Email Address"
          type="email"
          required
          fullWidth
          value={p1Values.email}
          onChange={handleChange('email')}
          errormsg={errors.email}
          onBlur={() => {
            if (!validateEmail(p1Values.email)) {
              setErrors((latestErrors) => {
                return {
                  ...latestErrors,
                  email: 'Invalid email address.'
                }
              })
            }
          }}
          option={
            <Typography variant="body3">
              By creating an Orka account, I agree to Orka’s{' '}
              <Link href={'/legal/PrivacyPolicy'}>
                <Typography variant="Link">Privacy Policy</Typography>
              </Link>{' '}
              and{' '}
              <Link href={'/legal/TermsOfService'}>
                <Typography variant="Link">Terms of Service</Typography>
              </Link>
              .
            </Typography>
          }
          optionProps={{
            error: errors.checked,
            errormsg: errors.checked
              ? 'Please agree to our terms of services.'
              : '',
            onClick: (e) => handleChecked(e),
            checked: p1Values.checkbox
          }}
        />
      </FormControl>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
          width: '100%'
        }}
      >
        <Button
          color="primary"
          variant="contained"
          size="large"
          handleClick={continueOnClick}
          loadable
          width="184px"
        >
          {i18n.t(k.CONTINUE1)}
        </Button>
        <Typography variant="body3">
          Already have an account?{' '}
          <Link href={'/SignIn'}>
            <Typography variant="Link" color={theme.palette.multicolor[300]}>
              Log In
            </Typography>
          </Link>
        </Typography>
      </div>
    </div>
  )
}

export default SignUp
